import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import "moment/locale/pt-br";
import "./style.css";
import axios from "axios";
import { FeedPostMenu } from "../shared";
import EditPostPopup from "../../editPostPopup";

export default function TutorialFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [difficulty] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingPost, setEditingPost] = useState(null);

  const categories = [
    { id: "all", name: "Todos" },
    { id: "programming", name: "Programação" },
    { id: "design", name: "Design" },
    { id: "video", name: "Vídeo" },
    { id: "music", name: "Música" },
    { id: "crafts", name: "Artesanato" },
    { id: "cooking", name: "Culinária" },
    { id: "other", name: "Outros" },
  ];

  const difficulties = [
    { id: "all", name: "Todos" },
    { id: "beginner", name: "Iniciante" },
    { id: "intermediate", name: "Intermediário" },
    { id: "advanced", name: "Avançado" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/feed/tutorial?page=${pageNum}&limit=12`;
      if (category !== "all") url += `&category=${category}`;
      if (difficulty !== "all") url += `&difficulty=${difficulty}`;

      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${user.token}` } });
      if (pageNum === 1) setPosts(data.posts || []);
      else setPosts((prev) => [...prev, ...(data.posts || [])]);
      setHasMore((data.pagination?.page || 1) < (data.pagination?.pages || 1));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching tutorials:", error);
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); fetchPosts(1); }, [category]);

  const handleEditPost = (post) => {
    setEditingPost(post);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts((prev) => prev.map((p) => (p._id === updatedPost._id ? updatedPost : p)));
    setEditingPost(null);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p._id !== postId));
  };

  const getDifficultyColor = (diff) => {
    switch (diff) {
      case "beginner": return "#4CAF50";
      case "intermediate": return "#FF9800";
      case "advanced": return "#F44336";
      default: return "var(--color-secondary)";
    }
  };

  if (loading && posts.length === 0) {
    return <div className="tutorial_feed_loading"><div className="tutorial_loader"></div><span>Carregando tutoriais...</span></div>;
  }

  return (
    <div className="tutorial_feed">
      {categories.length > 0 && (
        <div className="simple_filter_bar">
          <div className="filter_buttons">
            {categories.map((filter) => (
              <button
                key={filter.id}
                className={`filter_btn ${category === filter.id ? "active" : ""}`}
                onClick={() => setCategory(filter.id)}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="tutorial_feed_empty">
          <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
            <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
          </svg>
          <h3>Nenhum tutorial encontrado</h3>
          <p>Seja o primeiro a criar um tutorial!</p>
        </div>
      ) : (
        <div className="tutorial_grid">
          {posts.map((post) => (
            <div key={post._id} className="tutorial_card">
              <div className="tutorial_thumbnail">
                <img src={post.tutorialData?.thumbnail || post.images?.[0]?.url || post.images?.[0] || "/images/default-tutorial.png"} alt="" />
                {post.tutorialData?.videoUrl && (
                  <div className="tutorial_play_icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                )}
                <span className="tutorial_duration">{post.tutorialData?.duration || "N/A"}</span>
              </div>
              <div className="tutorial_card_content">
                <div className="tutorial_card_header">
                  <span className="tutorial_category">{categories.find(c => c.id === post.tutorialData?.category)?.name}</span>
                  <span className="tutorial_difficulty" style={{ color: getDifficultyColor(post.tutorialData?.difficulty) }}>
                    {difficulties.find(d => d.id === post.tutorialData?.difficulty)?.name}
                  </span>
                </div>
                <h3>{post.tutorialData?.title || post.text}</h3>
                <p className="tutorial_description">{post.tutorialData?.description}</p>
                <div className="tutorial_steps_preview">
                  <span>{post.tutorialData?.steps?.length || 0} passos</span>
                </div>
                <div className="tutorial_footer">
                  <Link to={`/profile/${post.user.username}`} className="tutorial_author">
                    <img src={post.user.picture} alt="" />
                    <span>{post.user.first_name}</span>
                  </Link>
                  <span className="tutorial_date">
                    <Moment fromNow locale="pt-br">{post.createdAt}</Moment>
                  </span>
                  <div onClick={(e) => e.stopPropagation()}>
                    <FeedPostMenu
                      post={post}
                      onEdit={handleEditPost}
                      onPostDeleted={handlePostDeleted}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="tutorial_load_more">
          <button onClick={() => { setPage(p => p + 1); fetchPosts(page + 1); }} disabled={loading}>
            {loading ? "Carregando..." : "Ver mais tutoriais"}
          </button>
        </div>
      )}
      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={(visible) => !visible && setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
